import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder } from "@angular/forms";
import { Observable, Subscription, BehaviorSubject, forkJoin } from "rxjs";
import { tap, finalize } from "rxjs/operators";
import { SystemSetting, UpdateSettingRequest } from "../../models/system-setting.model";
import { SubHeader } from "../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { SystemSettingsService } from "../../services/system-settings.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { LoaderService } from "../../../common/services/loader.service";
import { AuthService } from "../../../services/auth.service";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";

@Component({
    selector: 'app-system-settings',
    templateUrl: './system-settings.component.html',
    styleUrls: ['./system-settings.component.scss'],
    standalone: false
})
export class SystemSettingsComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

	private _subscription: Subscription = new Subscription();
	private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	private _currentSettings: SystemSetting[] = [];

	private _header = new SubHeader({
		title: "System Settings", buttons: [{ name: "Save Settings", method: () => this.save() }]
	})

	get currentSettings(): SystemSetting[] {
		if (this._currentSettings == null) {
			this._currentSettings = [];
		}
		return this._currentSettings;
	}

	get isLoading(): boolean {
		return this._loading.value;
	}

	form: FormGroup = new FormGroup({});

	constructor(private _builder: FormBuilder, private _settings: SystemSettingsService, _dialog: DynamicDialogService, private _subheaderService: SubHeaderService, private _loader: LoaderService, _auth: AuthService) {
		super(_dialog, _auth);
	}

	ngOnInit() {
		this.confirmDialog = new Dialog("Any unsaved changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
		let data$: Observable<SystemSetting[]> = this._settings.getAll().pipe(tap((settings: SystemSetting[]) => {
			this._currentSettings = settings;
		}));
		let subs$ = data$.pipe(
			finalize(() => { subs$.unsubscribe(); })
		).subscribe(
			() => {
				this.form = this.buildForm();
				console.log('form defaults', this.form);
				this._loading.next(false);
				this.allowDeactivate.next(true);
			},
			(err) => {
				console.error(err);
				this.dialogService.showError();
			}
		);
		
		this._subheaderService.updateSubHeader(this._header);

	}

	ngOnDestroy() {
		if (this._subscription != null) {
			this._subscription.unsubscribe();
		}
	}

	buildForm(): FormGroup {
		let group = {};
		this.currentSettings.forEach(setting => {
			if (setting.isActive != null) {
				group[setting.id] = [{ value: setting.isActive, disabled: true }, []]
			} else {
				group[setting.id] = [{ value: setting.settingState, disabled: true	}, []]
			}
		})
		return this._builder.group(group);
	}

	change(setting: SystemSetting, value?: boolean): void {
		if (value != null) {
			let ctrl = this.form.get(setting.id.toString());
			if (ctrl != null && ctrl.enabled) {
				ctrl.setValue(value);				
			}
		}
		this.setDirty();
	}

	editSetting(setting: SystemSetting): void {
		let ctrl = this.form.get(setting.id.toString());
		if (ctrl != null) {
			if (ctrl.enabled) {
				ctrl.disable();
			} else {
				ctrl.enable();
			}
		}
	}	

	save(): void {
		let request: UpdateSettingRequest = {
			settings: []
		}
		this.currentSettings.forEach(c => {
			let ctrl = this.form.get(c.id.toString());
			if (ctrl != null) {
				if (c.isActive != null) {
					c.isActive = <boolean>ctrl.value;
				} else {
					c.settingState = ctrl.value;
				}
				request.settings.push(c);
			}
		});
		this._loader.toggleLoader(true);
		let save$ = this._settings.save(request).pipe()
			.subscribe(
				(settings: SystemSetting[]) => {
					this._loader.toggleLoader(false);
					this.dialogService.showSuccessDialog();		
					this.allowDeactivate.next(true);
				},
				(err) => {
					console.error(err);
					this._loader.toggleLoader(false);
					this.dialogService.showError();
				}
		);
		this._subscription.add(save$);
	}

	setDirty(): void {
		this.allowDeactivate.next(false);
	}
}
